import { useRouter } from 'next/navigation';
import { Star, Heart, Loader2, ChevronDown } from 'lucide-react';
import { getThumbnailUrl, IMAGE_PLACEHOLDER } from '@/utils/imageUrl';
import { useEducationWishlistMutation } from '@/api/mutations/educationWishlist';
import { useAuth } from '@/context/Auth';
import { capitalizeFirst } from '@/utils/capitalizeFirst';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RatingDetailsPopover } from '@/components/pages/education/details/reviews/components/RatingDetailsPopover';
import { useIsPublic } from '@/context/PublicMode';
import { useLogoDetection } from '@/hooks/use-image-analysis';
import { useEffect, useMemo, useState } from 'react';
import { IEducation } from '../types';

interface EducationCardProps {
  education: IEducation;
  index: number;
  isLast?: boolean;
  isClickable: boolean;
  onWishlistRemove?: () => void;
}

export function EducationCard({
  education,
  index,
  isLast,
  isClickable = true,
  onWishlistRemove,
}: EducationCardProps) {
  const router = useRouter();
  const wishlistMutation = useEducationWishlistMutation();
  const { user } = useAuth();
  const isPublic = useIsPublic();

  const [isOnWishlist, setIsOnWishlist] = useState(education.isOnWishlist);
  const isOwner = user && education.userID === user.sub;
  const { isLogo, bgColor, handleImageLoad } = useLogoDetection('#ffffff');

  // Ordered fallback chain (like the business card): thumbnail (with CORS so the
  // logo-detection canvas can read pixels) → original → placeholder.
  const fallbacks = useMemo(() => {
    const steps: { src: string; cors: boolean }[] = [];
    if (education.thumbnailImage) {
      const thumb = getThumbnailUrl(education.thumbnailImage);
      steps.push({ src: thumb, cors: true }); // 1. thumbnail
      steps.push({ src: education.thumbnailImage, cors: false }); // 2. thumb 403 → original
    }
    steps.push({ src: IMAGE_PLACEHOLDER, cors: false }); // 3. last resort
    return steps;
  }, [education.thumbnailImage]);

  const [stepIdx, setStepIdx] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const current = fallbacks[stepIdx] ?? fallbacks[fallbacks.length - 1];

  useEffect(() => {
    setStepIdx(0);
    setImgLoaded(false);
  }, [fallbacks]);

  const handleImgError = () => {
    setStepIdx((i) => Math.min(i + 1, fallbacks.length - 1));
  };

  useEffect(() => {
    setIsOnWishlist(education.isOnWishlist);
  }, [education.isOnWishlist]);

  const handleDetailsClick = () => {
    if (isPublic) {
      if (isClickable)
        router.push(`/public/education/${education.id}?index=${index + 1}`);
    } else {
      router.push(`/education/details/${education.id}?index=${index + 1}`);
    }
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const previous = isOnWishlist;
    setIsOnWishlist(!isOnWishlist);

    wishlistMutation.mutate(
      {
        educationID: education.id,
        isOnWishlist: isOnWishlist || false,
      },
      {
        onSuccess: () => {
          if (isOnWishlist && onWishlistRemove) {
            onWishlistRemove();
          }
        },
        onError: () => setIsOnWishlist(previous),
      }
    );
  };

  const ReviewData = education.reviews
    ? {
        averageReviewScore: education.reviews.averageReviewScore,
        reviewsCount: education.reviews.reviewsCount,
        starDistribution: {
          '5 star': education.reviews.fiveStarPercentage,
          '4 star': education.reviews.fourStarPercentage,
          '3 star': education.reviews.threeStarPercentage,
          '2 star': education.reviews.twoStarPercentage,
          '1 star': education.reviews.oneStarPercentage,
        },
      }
    : null;

  return (
    <div
      className={
        isPublic
          ? isClickable
            ? 'flex h-80 hover:ring-4 transition-all duration-200 hover:ring-primary hover:ring-offset-1 hover:shadow-sm cursor-pointer'
            : 'flex h-80 cursor-not-allowed'
          : 'cursor-pointer'
      }
      onClick={handleDetailsClick}
    >
      <div className="flex w-[190px] mr-3 flex-col items-start gap-4 relative">
        {!isOwner && !isPublic && (
          <button
            onClick={handleFavoriteClick}
            // Icon only, one per card, so a listing announced as a run of
            // identical bare "button"s with no idea which school each belonged
            // to. aria-pressed carries the state the fill colour conveys.
            aria-label={
              isOnWishlist
                ? `Remove ${education.name} from favorites`
                : `Add ${education.name} to favorites`
            }
            aria-pressed={!!isOnWishlist}
            className="absolute cursor-pointer top-0 right-0 bg-[#948780] rounded-full p-2 shadow-md hover:bg-[#9f938d] transition-shadow z-10"
            disabled={wishlistMutation.isPending}
          >
            {wishlistMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
            ) : (
              <Heart
                className={` w-4 h-4 ${
                  isOnWishlist
                    ? 'text-red-500 fill-red-500'
                    : 'text-white fill-white'
                }`}
              />
            )}
          </button>
        )}
        {isPublic && !isClickable && (
          <span className="absolute top-1 right-1 bg-white rounded-full px-2 py-0.5 shadow-md text-[10px] font-semibold text-[#554E49] z-10">
            Members Only
          </span>
        )}

        <div className="flex items-start gap-5">
          <div className="flex-shrink-0 w-8 h-8 flex justify-start items-start">
            <span className="text-[#554E49] text-3xl font-medium">
              {index !== undefined ? index + 1 : null}
            </span>
          </div>

          <div
            className="flex-shrink-0 w-24 h-24 border border-gray-100 rounded-lg overflow-hidden relative"
            style={{ backgroundColor: bgColor }}
          >
            {education.thumbnailImage ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                key={stepIdx}
                src={current.src}
                crossOrigin={current.cors ? 'anonymous' : undefined}
                alt={`${education.name} logo`}
                loading="lazy"
                decoding="async"
                onLoad={(e) => {
                  setImgLoaded(true);
                  handleImageLoad(e.currentTarget);
                }}
                onError={handleImgError}
                className={`absolute inset-0 h-full w-full ${
                  isLogo ? 'object-contain' : 'object-cover'
                } transition-opacity duration-300 ${
                  imgLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ) : (
              <div className="w-full h-full bg-gray-50 flex items-center justify-center rounded-lg">
                <span className="text-gray-400 text-sm font-medium">
                  {education.name.substring(0, 3).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0 flex flex-col h-full w-full">
          <div className="flex-1">
            <h3
              className={`font-bold capitalize text-lg text-black mb-2 line-clamp-1 ${
                isClickable
                  ? 'group-hover:text-[#554E49] text-xl group-hover:font-extrabold transition-all duration-200'
                  : 'opacity-70 italic '
              }`}
            >
              {education.name}
            </h3>

            <p
              className={`text-black text-sm line-clamp-6 mb-3 ${
                isClickable
                  ? 'group-hover:text-[#554E49] text-md font-semibold group-hover:font-extrabold transition-all duration-200'
                  : 'opacity-70 italic'
              }`}
            >
              {capitalizeFirst(education.overview)}
            </p>
          </div>

          <div className="flex items-center justify-between w-full">
            <button
              type="button"
              onClick={handleDetailsClick}
              aria-label={`View details for ${education.name}`}
              className="bg-[#948780] cursor-pointer hover:opacity-80 text-black font-bold py-2 px-3 rounded-3xl transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Details
            </button>

            {education.reviews && education.reviews.averageReviewScore ? (
              <div className="flex items-center">
                {education.reviews.averageReviewScore &&
                  (parseFloat(education.reviews.averageReviewScore.toString()) >
                  0 ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-1 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded-md"
                          aria-label={`View rating details for ${education.name}: ${typeof education.averageReviewScore === 'number' ? education.averageReviewScore.toFixed(1) : parseFloat(education.reviews.averageReviewScore.toString()).toFixed(1)} stars from ${education.reviews.reviewsCount} reviews`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Star className="w-4 h-4 mr-1 fill-[#948780] text-[#554E49]" aria-hidden="true" />
                          <span className="font-medium text-black">
                            {typeof education.averageReviewScore === 'number'
                              ? education.averageReviewScore.toFixed(1)
                              : parseFloat(
                                  education.reviews.averageReviewScore.toString()
                                ).toFixed(1)}
                          </span>
                          <span>{`(${education.reviews.reviewsCount})`}</span>
                          <ChevronDown className="w-4 h-4 ml-1" aria-hidden="true" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0 z-50 shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {ReviewData && (
                          <RatingDetailsPopover
                            data={ReviewData}
                            reviewsLink={`/education/details/${education.id}#reviews`}
                          />
                        )}
                      </PopoverContent>
                    </Popover>
                  ) : null)}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {!isLast && <div className="border-l-2 border-[#d7d0cc]" />}
    </div>
  );
}
